import axios from "axios";
import toast from "react-hot-toast";
import { jwtDecode } from "jwt-decode";

const API = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

API.interceptors.request.use(
  (config) => {
    // keep your current auth header
    const authToken = localStorage.getItem("auth");
    if (authToken) config.headers.Authorization = `Bearer ${authToken}`;

    // slug from localStorage, subdomain, or .env
    // Check localStorage first, then extract from subdomain for public pages
    let slug =
      localStorage.getItem("churchSlug") ||
      localStorage.getItem("atriumChurchSlug") ||
      process.env.REACT_APP_CHURCH_SLUG;

    // If no slug in localStorage, extract from subdomain (for public pages)
    if (!slug) {
      const hostname = window.location.hostname;
      const parts = hostname.split(".");
      // e.g., "atriumdemo.localhost" or "churchname.atriumai.co"
      if (parts.length >= 2 && parts[0] !== "www" && parts[0] !== "app") {
        slug = parts[0];
      }
    }

    if (slug) {
      config.headers["X-Church-Slug"] = slug;
    } else {
      console.warn("⚠️ No church slug found. API requests may fail.");
    }

    // If sending FormData, remove Content-Type header and let axios set it with boundary
    if (config.data instanceof FormData) {
      console.log("🔍 FormData detected, removing Content-Type header");
      console.log("FormData contents:");
      for (let pair of config.data.entries()) {
        console.log(pair[0], pair[1]);
      }
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Shared refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const onRefreshFailed = (err) => {
  refreshSubscribers.forEach((cb) => cb(null, err));
  refreshSubscribers = [];
};

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

// Response interceptor for session expiration handling
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status || error.status;

    if (status === 401) {
      // Check if we have a valid (non-expired) token before killing the session.
      // A stale request can return 401 even though the user just logged in
      // (e.g., a late-arriving 401 from a request sent before the token was refreshed).
      try {
        const raw = localStorage.getItem("auth");
        if (raw) {
          const currentToken = raw.startsWith('"') ? JSON.parse(raw) : raw;
          const decoded = jwtDecode(currentToken);
          if (decoded.exp && decoded.exp * 1000 > Date.now()) {
            // Token was refreshed while this request was in-flight — retry with the new token
            error.config.headers["Authorization"] = `Bearer ${currentToken}`;
            return API(error.config);
          }
        }
      } catch (e) {
        if (e?.config) throw e;
      }

      const originalConfig = error.config;

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token, err) => {
            if (err || !token) return reject(error);
            originalConfig.headers["Authorization"] = `Bearer ${token}`;
            resolve(API(originalConfig));
          });
        });
      }

      isRefreshing = true;

      try {
        // Include church slug in refresh request
        const slug =
          localStorage.getItem("churchSlug") ||
          localStorage.getItem("atriumChurchSlug") ||
          process.env.REACT_APP_CHURCH_SLUG ||
          (() => {
            const parts = window.location.hostname.split(".");
            return parts.length >= 2 && parts[0] !== "www" && parts[0] !== "app" ? parts[0] : null;
          })();

        const response = await axios.post(
          "/api/v1/refresh-token",
          {},
          {
            baseURL: process.env.REACT_APP_BASE_URL,
            withCredentials: true,
            headers: {
              ...(slug && { "X-Church-Slug": slug }),
            },
          }
        );
        if (response?.data?.token) {
          const user = jwtDecode(response.data.token);
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("auth", response.data.token);
          originalConfig.headers["Authorization"] = `Bearer ${response.data.token}`;
          onRefreshed(response.data.token);
          isRefreshing = false;
          return await API(originalConfig);
        }
      } catch (refreshError) {
        console.log("Token refresh failed, redirecting to login");
        onRefreshFailed(refreshError);
      }

      isRefreshing = false;

      // Refresh failed - logout and redirect (preserve slug for login page)
      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      localStorage.removeItem("atriumAuthToken");
      localStorage.removeItem("atriumUser");

      if (!window.location.pathname.startsWith("/auth/")) {
        toast.error("Session has expired. Please log in again.");
        window.location.href = "/auth/signin";
      }
      throw error;
    } else if (status === 422) {
      return error.response;
    }
    throw error;
  }
);

export default API;
